import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Copy, ArrowSquareOut, PencilSimple, Check, X } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [trust, setTrust] = useState(null);
  const [editSlug, setEditSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [slugErr, setSlugErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/settings"); setS(r.data);
      try {
        const t = await api.get("/trust-center"); setTrust(t.data); setSlugDraft(t.data.slug);
      } catch (err) {
        console.warn("[Settings] failed to load trust center:", err?.message || err);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", {
        alcohol_flag: s.alcohol_flag,
        bik_threshold: Number(s.bik_threshold),
        vat_standard: Number(s.vat_standard),
        vat_reduced: Number(s.vat_reduced),
        weekly_digest_enabled: !!s.weekly_digest_enabled,
        weekly_digest_email: s.weekly_digest_email || "",
      });
      setS(r.data);
      alert("Rules updated");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-white/50">Loading…</div>;

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="eyebrow mb-2">Settings</div>
        <h1 className="font-display text-4xl">Compliance rules engine</h1>
        <p className="text-white/60 text-sm mt-2">Tune Irish Revenue.ie thresholds. Changes are audit-logged.</p>
      </div>

      <div className="glass rounded-xl p-6 space-y-5">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <div className="text-sm font-semibold text-white/90">Flag alcohol on receipts</div>
            <div className="text-xs text-white/50 mt-0.5">Irish Revenue: alcohol is generally non-deductible for tax.</div>
          </div>
          <input type="checkbox" checked={s.alcohol_flag} onChange={e => setS({...s, alcohol_flag: e.target.checked})} className="w-auto scale-125" data-testid="toggle-alcohol"/>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-white/5">
          <div>
            <label>BIK threshold (€)</label>
            <input type="number" step="0.01" value={s.bik_threshold} onChange={e => setS({...s, bik_threshold: e.target.value})} data-testid="input-bik"/>
          </div>
          <div>
            <label>VAT Standard (%)</label>
            <input type="number" step="0.1" value={s.vat_standard} onChange={e => setS({...s, vat_standard: e.target.value})} data-testid="input-vat-std"/>
          </div>
          <div>
            <label>VAT Reduced (%)</label>
            <input type="number" step="0.1" value={s.vat_reduced} onChange={e => setS({...s, vat_reduced: e.target.value})} data-testid="input-vat-red"/>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-white/5">
          <button data-testid="btn-save-settings" onClick={save} disabled={saving} className="btn-gold">{saving ? "Saving…" : "Save rules"}</button>
        </div>
      </div>

      <div className="glass rounded-xl p-6 space-y-5" data-testid="settings-digest-card">
        <div>
          <div className="eyebrow">Weekly Digest</div>
          <div className="text-sm text-white/70 mt-1">Every Sunday morning, get a one-email summary of the week&apos;s telemetry — receipts, certificates, exports and emails — with your latest Report Card PDF attached.</div>
        </div>
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <div className="text-sm font-semibold text-white/90">Email me every Sunday 08:00 (Dublin)</div>
            <div className="text-xs text-white/50 mt-0.5">Skip if you were already emailed this week.</div>
          </div>
          <input type="checkbox" checked={!!s.weekly_digest_enabled} onChange={e => setS({...s, weekly_digest_enabled: e.target.checked})} className="w-auto scale-125" data-testid="toggle-digest"/>
        </label>
        <div>
          <label>Recipient email (defaults to your account email)</label>
          <input type="email" placeholder={s.weekly_digest_email ? "" : "you@company.com"}
            value={s.weekly_digest_email || ""}
            onChange={e => setS({...s, weekly_digest_email: e.target.value})}
            data-testid="input-digest-email"/>
        </div>
        {s.weekly_digest_last_sent && (
          <div className="text-xs text-white/45 mono">Last sent: {s.weekly_digest_last_sent.slice(0, 19).replace("T", " ")} UTC</div>
        )}
      </div>

      <div className="glass rounded-xl p-6 space-y-5" data-testid="settings-trust-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
            <ShieldCheck size={20} weight="duotone" color="#050B0A" />
          </div>
          <div>
            <div className="eyebrow">Public Trust Center</div>
            <div className="text-sm text-white/70 mt-0.5">One live URL to share with grant bodies, investors and auditors — auto-generated from your compliance data.</div>
          </div>
        </div>

        {!trust ? (
          <div className="text-white/45 text-xs">Loading trust page…</div>
        ) : (
          <>
            <div>
              <label>Trust slug</label>
              {editSlug ? (
                <div className="flex gap-2">
                  <input
                    value={slugDraft}
                    onChange={e => setSlugDraft(e.target.value.toLowerCase())}
                    data-testid="input-trust-slug"
                    placeholder="e.g. acme-labs"
                    className="flex-1"
                  />
                  <button
                    data-testid="btn-save-slug"
                    className="btn-gold text-sm"
                    onClick={async () => {
                      setSlugErr("");
                      try {
                        await api.put("/trust-center/slug", { slug: slugDraft });
                        const t = await api.get("/trust-center"); setTrust(t.data); setEditSlug(false);
                      } catch (e) { setSlugErr(e?.response?.data?.detail || e.message); }
                    }}
                  ><Check size={14} weight="bold" /> Save</button>
                  <button
                    className="btn-ghost text-sm"
                    onClick={() => { setEditSlug(false); setSlugDraft(trust.slug); setSlugErr(""); }}
                  ><X size={14} weight="bold" /> Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <code className="mono text-sm px-3 py-2 bg-black/30 border border-white/10 rounded-lg flex-1" data-testid="trust-slug-display">{trust.slug}</code>
                  <button data-testid="btn-edit-slug" onClick={() => setEditSlug(true)} className="btn-ghost text-sm">
                    <PencilSimple size={13} weight="bold" /> Change
                  </button>
                </div>
              )}
              {slugErr && <div className="text-[#E63946] text-xs mt-2" data-testid="slug-err">{slugErr}</div>}
              <div className="text-xs text-white/45 mt-2">3–32 chars, lowercase letters, digits, hyphens.</div>
            </div>

            <div>
              <label>Public URL</label>
              <div className="flex items-center gap-2">
                <code className="mono text-xs px-3 py-2 bg-black/30 border border-white/10 rounded-lg flex-1 truncate" data-testid="trust-public-url">{trust.public_url}</code>
                <button
                  data-testid="btn-copy-trust-url"
                  className="btn-ghost text-xs"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(trust.public_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (err) { console.warn("[Settings] clipboard copy failed:", err?.message || err); }
                  }}
                ><Copy size={13} weight="bold" /> {copied ? "Copied" : "Copy"}</button>
                <a
                  data-testid="btn-open-trust"
                  href={trust.public_url}
                  target="_blank" rel="noopener noreferrer"
                  className="btn-gold text-xs"
                ><ArrowSquareOut size={13} weight="fill" /> Open</a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
